import { inject, Injectable, NgZone } from "@angular/core";
import {
  BASE_ENVIRONMENT,
  convertMinutesToMilliseconds,
  IdlePeriod,
  VoidFunction,
} from "blu-utilities";
import { COOKIES } from "../models/cookie";

/**
 * Tracks raw user interaction and persists the latest activity timestamp.
 *
 * This service does not decide when to logout the user. It only answers:
 * "when was the last meaningful interaction?"
 *
 * SessionTimeoutService consumes that timestamp and applies the timeout policy.
 */
@Injectable({ providedIn: "root" })
export class ActivityService {
  private readonly zone = inject(NgZone);
  private readonly environment = inject(BASE_ENVIRONMENT);

  private unSubscriptions: VoidFunction[] = [];
  private isAlreadyStarted = false;
  private readonly activityEvents = [
    "click",
    "keydown",
    "touchstart",
    "mousemove",
    "scroll",
  ] as const;

  /**
   * Time in minutes before user is considered idle
   * Defaults to 15 minutes if not specified in environment
   */
  readonly idlePeriod =
    this.environment.idlePeriod ?? IdlePeriod.FIFTEEN_MINUTES;

  /**
   * Reads the latest activity timestamp from localStorage so the value is shared
   * across tabs of the same application.
   */
  get lastActivityAt(): number {
    try {
      const lastActivityAt = localStorage.getItem(
        COOKIES.LAST_USER_ACTIVITY_AT,
      );
      return lastActivityAt ? +lastActivityAt : 0;
    } catch {
      return 0;
    }
  }

  /**
   * Sets up activity tracking by registering event listeners
   * Runs outside Angular zone for better performance
   */
  initialize(): void {
    if (this.isAlreadyStarted) {
      return;
    }

    this.isAlreadyStarted = true;
    this.zone.runOutsideAngular(() => {
      this.activityEvents.forEach((event) => {
        const handler = () => this.setLastUserActivity();
        window.addEventListener(event, handler, { passive: true });
        this.unSubscriptions.push(() =>
          window.removeEventListener(event, handler),
        );
      });

      // Seed the timestamp so timeout checks have a baseline immediately.
      this.setLastUserActivity();
    });
  }

  /**
   * Updates the last activity timestamp in localStorage
   * Called automatically on user interaction events
   */
  setLastUserActivity(): void {
    try {
      localStorage.setItem(COOKIES.LAST_USER_ACTIVITY_AT, String(Date.now()));
    } catch {
      // localStorage can throw in private mode; fail silently
    }
  }

  /**
   * Checks if user has been inactive longer than idlePeriod
   * @returns true if user is considered idle, false otherwise
   */
  isIdleDetected(): boolean {
    const lastActivity = this.lastActivityAt;

    // Seed missing storage lazily so first checks do not force a logout.
    if (!lastActivity) {
      this.setLastUserActivity();
      return false;
    }

    const timeElapsed = Date.now() - lastActivity;
    return timeElapsed >= convertMinutesToMilliseconds(this.idlePeriod);
  }

  /**
   * Cleanup method to remove event listeners and stored data.
   */
  destroy(): void {
    this.unSubscriptions.forEach((unSubscription) => unSubscription());
    this.unSubscriptions = [];
    this.isAlreadyStarted = false;
    try {
      localStorage.removeItem(COOKIES.LAST_USER_ACTIVITY_AT);
    } catch {
      // localStorage can throw in private mode; fail silently
    }
  }
}
